/**
 * Invitations under the users list (spec §6.3 / §7): email, role, status, expiry, last sent with
 * count; Send / Resend (new link), Copy link, Revoke, and New invitation on expired rows. The View
 * is pure (node vitest), and so is the container: the page owns the list, the reads and the
 * invitation actions for its current org and passes them in, so a tab switch, which unmounts this
 * panel, loses nothing.
 */
import { Check, Copy, Plus, Send, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import type { AdminInvitation } from '../services/adminApi';
import { formatDate } from './formatDate';
import {
  formatLastSent,
  invitationRowActions,
  invitationStatusBadgeClass,
  invitationStatusLabel,
  sendBlockMessage,
  usableAcceptUrl,
  type SendBlock,
} from './orgProvisioning';
import { RECONCILE_FAILED_MESSAGE, formatSentCue, type PendingAction, type SentCue } from './invitationActions';
import type { UseInvitationActions } from './useInvitationActions';

const TITLE_ID = 'invitations-title';

/**
 * The part of a block's message the live region announces. A cooldown's countdown would make the
 * region re-announce every second, so the trailing " in N s" stays in the visible line only.
 */
function announcedBlockMessage(block: SendBlock): string {
  const message = sendBlockMessage(block);
  return block.kind === 'cooldown' ? message.replace(/ in \d+ s$/, '') : message;
}

const ALERT_CLASS =
  'mb-3 flex items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive';

export interface InvitationsPanelViewProps {
  invitations: AdminInvitation[];
  now: Date;
  /** The list failed to load; shown with Retry, and the empty-state row is suppressed. */
  loadError: string | null;
  /** A send, copy or revoke failed; shown in its own alert so neither error hides the other. */
  actionError: string | null;
  /** Invitation ids with an action in flight (send, revoke, copy or the post-failure refresh); every control on that row is disabled. */
  pending: Record<string, PendingAction>;
  /** Ids whose refresh after a failed send failed: the row stays disabled and offers Retry. */
  reconcileFailed: Record<string, true>;
  copiedInvitationId: string | null;
  /**
   * The invitation whose link the clipboard refused; its CURRENT acceptUrl is shown read-only only
   * while that row is unheld (no `pending` entry, not `reconcileFailed`) and pending and unexpired
   * (`usableAcceptUrl`), so a held row never shows a link the server may have rotated away.
   */
  copyFallbackInvitationId: string | null;
  sentCue: SentCue | null;
  /** Per invitation: a 429 cooldown counting down, or the 409 send limit; Send is disabled while set. */
  sendBlocks: Record<string, SendBlock>;
  onRetryLoad: () => void;
  onSend: (invitation: AdminInvitation) => void;
  onCopyLink: (invitation: AdminInvitation) => void;
  onRetryReconcile: (invitation: AdminInvitation) => void;
  onRevoke: (invitation: AdminInvitation) => void;
  onNewInvitation: (invitation: AdminInvitation) => void;
}

export function InvitationsPanelView({
  invitations,
  now,
  loadError,
  actionError,
  pending,
  reconcileFailed,
  copiedInvitationId,
  copyFallbackInvitationId,
  sentCue,
  sendBlocks,
  onRetryLoad,
  onSend,
  onCopyLink,
  onRetryReconcile,
  onRevoke,
  onNewInvitation,
}: InvitationsPanelViewProps) {
  return (
    <Card className="info-section">
      <h3 id={TITLE_ID}>Invitations</h3>
      {loadError && (
        <div role="alert" className={ALERT_CLASS}>
          <span>{loadError}</span>
          <Button type="button" variant="ghost" size="sm" onClick={onRetryLoad}>Retry</Button>
        </div>
      )}
      {actionError && (
        <div role="alert" className={ALERT_CLASS}>
          <span>{actionError}</span>
        </div>
      )}
      <div aria-live="polite" className="mb-2 text-sm text-muted-foreground">
        {sentCue ? formatSentCue(sentCue) : null}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="admin-table invitations-table" aria-labelledby={TITLE_ID}>
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Expires</th>
              <th>Last sent</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invitations.map((inv) => {
              const label = invitationStatusLabel(inv, now);
              const actions = invitationRowActions(inv, now);
              const refreshFailed = inv.id in reconcileFailed;
              const rowBusy = inv.id in pending || refreshFailed;
              const sending = pending[inv.id] === 'send';
              const copied = copiedInvitationId === inv.id;
              // Read from the CURRENT row, so a reload after a resend shows the new link, and
              // nothing once the row turned terminal (its link is dead). No link on a held row: nothing
              // either while the row is held or its refresh failed, because the link may be the
              // one the server just rotated away; the id is kept, so the fresh link reappears.
              const fallbackUrl =
                copyFallbackInvitationId === inv.id && !rowBusy ? usableAcceptUrl(inv, now) : null;
              const block = sendBlocks[inv.id] ?? null;
              const sendDisabled = rowBusy || block !== null;
              return (
                <tr key={inv.id}>
                  <td title={inv.email}>{inv.email}</td>
                  <td>{inv.role}</td>
                  <td>
                    <Badge variant="outline" className={invitationStatusBadgeClass(label)}>
                      {label}
                    </Badge>
                  </td>
                  <td>{formatDate(inv.expiresAt)}</td>
                  <td>{formatLastSent(inv)}</td>
                  <td>
                    <div className="action-buttons">
                      {actions.includes('send') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={sendDisabled}
                          aria-label={`${sending ? 'Sending' : 'Send'} invitation to ${inv.email}`}
                          onClick={() => onSend(inv)}
                        >
                          <Send />
                          {sending ? 'Sending…' : 'Send'}
                        </Button>
                      )}
                      {actions.includes('resend') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={sendDisabled}
                          aria-label={sending ? `Sending invitation to ${inv.email}` : `Resend invitation to ${inv.email} (new link)`}
                          onClick={() => onSend(inv)}
                        >
                          <Send />
                          {sending ? 'Sending…' : 'Resend (new link)'}
                        </Button>
                      )}
                      {actions.includes('copy') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={rowBusy}
                          aria-label={copied ? `Link copied for ${inv.email}` : `Copy invitation link for ${inv.email}`}
                          onClick={() => onCopyLink(inv)}
                        >
                          {copied ? <Check /> : <Copy />}
                          <span aria-live="polite">{copied ? 'Copied' : 'Copy link'}</span>
                        </Button>
                      )}
                      {actions.includes('revoke') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={rowBusy}
                          aria-label={`Revoke invitation for ${inv.email}`}
                          onClick={() => onRevoke(inv)}
                        >
                          <Trash2 />
                          Revoke
                        </Button>
                      )}
                      {actions.includes('new') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`New invitation for ${inv.email}`}
                          onClick={() => onNewInvitation(inv)}
                        >
                          <Plus />
                          New invitation
                        </Button>
                      )}
                    </div>
                    {block && block.kind === 'cooldown' && (
                      <>
                        <span role="status" className="sr-only">{announcedBlockMessage(block)}</span>
                        <div className="send-block mt-1 text-sm text-muted-foreground">{sendBlockMessage(block)}</div>
                      </>
                    )}
                    {block && block.kind !== 'cooldown' && (
                      <div role="status" className="send-block mt-1 text-sm text-muted-foreground">{sendBlockMessage(block)}</div>
                    )}
                    {refreshFailed && (
                      <div role="alert" className="send-block mt-1 flex items-center gap-2 text-sm text-destructive">
                        <span>{RECONCILE_FAILED_MESSAGE}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Retry refreshing invitation for ${inv.email}`}
                          onClick={() => onRetryReconcile(inv)}
                        >
                          Retry
                        </Button>
                      </div>
                    )}
                    {fallbackUrl && (
                      <Input
                        readOnly
                        value={fallbackUrl}
                        aria-label="Invitation link"
                        className="mt-1"
                        onFocus={(e) => e.currentTarget.select()}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
            {invitations.length === 0 && !loadError && (
              <tr>
                <td colSpan={6} className="text-center text-muted-foreground">No invitations yet</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export interface InvitationsPanelProps {
  invitations: AdminInvitation[];
  loadError: string | null;
  /** Retry after a failed load; the parent calls fetchInvitations. */
  onRetryLoad: () => void;
  /**
   * The page's invitation actions for this org (`useInvitationActions`, one controller per org). The
   * panel mounts only while the Users tab is active, so it owns none of this state: holds,
   * reconcile failures, send keys, cooldowns, cues and the copy fallback live with the page and
   * survive a tab switch, and the page's callbacks apply send DTOs and run the awaited reloads.
   */
  actions: UseInvitationActions;
  /** Called from an expired row; the parent opens the invite dialog prefilled. */
  onNewInvitation: (invitation: AdminInvitation) => void;
}

export function InvitationsPanel({ invitations, loadError, onRetryLoad, actions, onNewInvitation }: InvitationsPanelProps) {
  return (
    <InvitationsPanelView
      invitations={invitations}
      now={new Date()}
      loadError={loadError}
      actionError={actions.actionError}
      pending={actions.pending}
      reconcileFailed={actions.reconcileFailed}
      copiedInvitationId={actions.copiedInvitationId}
      copyFallbackInvitationId={actions.copyFallbackInvitationId}
      sentCue={actions.sentCue}
      sendBlocks={actions.sendBlocks}
      onRetryLoad={onRetryLoad}
      onSend={(inv) => void actions.send(inv)}
      onCopyLink={(inv) => void actions.copyLink(inv)}
      onRetryReconcile={(inv) => void actions.retryReconcile(inv.id)}
      onRevoke={(inv) => void actions.revoke(inv)}
      onNewInvitation={onNewInvitation}
    />
  );
}
